// Get API base URL from environment variable
const API_BASE_URL = import.meta.env?.VITE_API_URL ?? "https://tellerwand.com";

export const API_ENDPOINTS = {
  SYSTEM: {
    API_REQUESTS: `${API_BASE_URL}/api/system/api-requests`,
    LOGS: `${API_BASE_URL}/api/system/logs`,
    LOGS_PURGE: `${API_BASE_URL}/api/system/logs/purge`,
    METRICS: `${API_BASE_URL}/api/system/metrics`,
    LATEST_METRICS: `${API_BASE_URL}/api/system/metrics/latest`,
    ANALYTICS_TRACK_EVENT: `${API_BASE_URL}/api/system/analytics/track-event`,
    ANALYTICS_LOG_ACTIVITY: `${API_BASE_URL}/api/system/analytics/log-activity`,
    ANALYTICS_USER_ACTIVITY: `${API_BASE_URL}/api/system/analytics/user-activity`,
    ANALYTICS_SESSION_ANALYTICS: `${API_BASE_URL}/api/system/analytics/session-analytics`,
  },
  ADMIN: {
    BASE: `${API_BASE_URL}/api/admin`,
    USERS: `${API_BASE_URL}/api/admin/users`,
    FIND_USER_BY_EMAIL: `${API_BASE_URL}/api/admin/users/find-by-email`,
    SUBSCRIPTIONS: `${API_BASE_URL}/api/admin/subscriptions`,
    ASSETS: `${API_BASE_URL}/api/admin/assets`,
    STATS: `${API_BASE_URL}/api/admin/stats`,
    STATS_V3: `${API_BASE_URL}/api/admin/stats/v3`,
    SUBSCRIPTION_STATS: `${API_BASE_URL}/api/admin/subscription-stats`,
    ASSET_STATS: `${API_BASE_URL}/api/admin/asset-stats`,
    FEATURE_FLAGS: `${API_BASE_URL}/api/admin/feature-flags`,
    CHECK_ADMIN: `${API_BASE_URL}/api/admin/users/check-admin`,
    CHECK_SUPER_ADMIN: `${API_BASE_URL}/api/admin/users/check-super-admin`,
    CREATE_USER: `${API_BASE_URL}/api/admin/users/create`,
    USER_ACTIVITY: `${API_BASE_URL}/api/admin/user-activity`,
    USER_SESSIONS: `${API_BASE_URL}/api/admin/user-sessions`,
    PLATFORM_AUDIT_LOG: `${API_BASE_URL}/api/admin/platform-audit-log`,
    AUDIT_COLD_ARCHIVES: `${API_BASE_URL}/api/admin/platform-audit-log/cold-archives`,
    SECURITY_EVENTS: `${API_BASE_URL}/api/admin/security-events`,
    DELETED_USERS: `${API_BASE_URL}/api/admin/deleted-users`,
  },
  AUTH: {
    BASE: `${API_BASE_URL}/api/v1/auth`,
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    REGISTER: `${API_BASE_URL}/api/auth/register`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    SESSIONS: `${API_BASE_URL}/api/v1/auth/sessions`,
    ENCRYPTION_KEYS: `${API_BASE_URL}/api/v1/auth/encryption-keys`,
    TRACK_REFERRAL_SIGNUP: `${API_BASE_URL}/api/v1/auth/utilities/track-referral-signup`,
    RECOVERY_CHECK_ELIGIBILITY: `${API_BASE_URL}/api/v1/auth/utilities/recovery/check-eligibility`,
    SECURITY_CHECK_LOCKOUT: `${API_BASE_URL}/api/v1/auth/security/check-lockout`,
    SECURITY_LOG_LOGIN: `${API_BASE_URL}/api/v1/auth/security/log-login`,
    SECURITY_LOG_EVENT: `${API_BASE_URL}/api/v1/auth/security/events/log`,
    SECURITY_RATE_LIMIT_CHECK: `${API_BASE_URL}/api/v1/auth/security/rate-limit/check`,
    SECURITY_RATE_LIMIT_INFO: `${API_BASE_URL}/api/v1/auth/security/rate-limit/info`,
    SECURITY_PAN_ENCRYPT: `${API_BASE_URL}/api/v1/auth/security/pan/encrypt`,
  },
  TRANSACTIONS: {
    LIST: `${API_BASE_URL}/api/transactions`,
    CREATE: `${API_BASE_URL}/api/transactions`,
    UPDATE: `${API_BASE_URL}/api/transactions`,
    DELETE: `${API_BASE_URL}/api/transactions`,
  },
  ASSETS: {
    LIST: `${API_BASE_URL}/api/assets`,
    CREATE: `${API_BASE_URL}/api/assets`,
    UPDATE: `${API_BASE_URL}/api/assets`,
    DELETE: `${API_BASE_URL}/api/assets`,
  },
  LIABILITIES: {
    LIST: `${API_BASE_URL}/api/liabilities`,
    CREATE: `${API_BASE_URL}/api/liabilities`,
    UPDATE: `${API_BASE_URL}/api/liabilities`,
    DELETE: `${API_BASE_URL}/api/liabilities`,
  },
  GOALS: {
    LIST: `${API_BASE_URL}/api/v1/goals`,
    CREATE: `${API_BASE_URL}/api/v1/goals`,
    UPDATE: `${API_BASE_URL}/api/v1/goals`,
    DELETE: `${API_BASE_URL}/api/v1/goals`,
    CONTRIBUTE: `${API_BASE_URL}/api/v1/goals`,
    PROGRESS: `${API_BASE_URL}/api/v1/goals`,
    STATS: `${API_BASE_URL}/api/v1/goals/stats`,
  },
  DASHBOARD: {
    BATCH: `${API_BASE_URL}/api/v1/dashboard`,
  },
  SETTINGS: {
    GET: `${API_BASE_URL}/api/settings`,
    UPDATE: `${API_BASE_URL}/api/settings`,
    PROFILE: `${API_BASE_URL}/api/settings/profile`,
    PREFERENCES: `${API_BASE_URL}/api/settings/preferences`,
    PRIVACY_CONSENT: `${API_BASE_URL}/api/settings/privacy-consent`,
    CATEGORIES: `${API_BASE_URL}/api/settings/categories`,
    CATEGORIES_SEARCH: `${API_BASE_URL}/api/settings/categories/search`,
    CATEGORY_IMAGE: (userId: string) =>
      `${API_BASE_URL}/api/settings/categories/${encodeURIComponent(userId)}/image`,
  },
  SUBSCRIPTIONS: {
    LIST: `${API_BASE_URL}/api/subscriptions`,
    CREATE: `${API_BASE_URL}/api/subscriptions`,
    UPDATE: `${API_BASE_URL}/api/subscriptions`,
    EXTEND: `${API_BASE_URL}/api/subscriptions/extend`,
    CHECK_FREE_MONTHS: `${API_BASE_URL}/api/subscriptions/check-free-months`,
    END_DATE: `${API_BASE_URL}/api/subscriptions/end-date`,
    COMPANY_LINK: `${API_BASE_URL}/api/subscriptions/company-link`,
    COMPANY_UNLINK: `${API_BASE_URL}/api/subscriptions/company-unlink`,
    COMPANY_LINK_CURRENT: (userId: string) =>
      `${API_BASE_URL}/api/subscriptions/company-link/current/${userId}`,
    COMPANY_LINK_VALIDATE: (code: string) =>
      `${API_BASE_URL}/api/subscriptions/company-link/validate?code=${encodeURIComponent(code)}`,
    COMPANY_MY_COMPANIES: `${API_BASE_URL}/api/subscriptions/company/my-companies`,
    COMPANY_CREATE: `${API_BASE_URL}/api/subscriptions/company/create`,
    COMPANY_VERIFY: `${API_BASE_URL}/api/subscriptions/company/verify`,
    COMPANY_LINK_DEPENDENTS: `${API_BASE_URL}/api/subscriptions/company-link/dependents`,
    COMPANY_LINK_DEPENDENT_VALIDATE: (code: string) =>
      `${API_BASE_URL}/api/subscriptions/company-link/dependent-invite/validate?code=${encodeURIComponent(code)}`,
    COMPANY_LINK_DEPENDENT_ACCEPT: `${API_BASE_URL}/api/subscriptions/company-link/dependent-invite/accept`,
    COMPANY_LINK_DEPENDENT_REMOVE: (dependentId: string) =>
      `${API_BASE_URL}/api/subscriptions/company-link/dependents/${encodeURIComponent(dependentId)}/remove`,
    COMPANY_HR_CHECK: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/check`,
    COMPANY_HR_DIRECTORY: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/directory`,
    COMPANY_HR_STATS: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/stats`,
    COMPANY_HR_PENDING: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/pending`,
    COMPANY_HR_APPROVE: (slug: string, linkId: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/pending/${encodeURIComponent(linkId)}/approve`,
    COMPANY_HR_DENY: (slug: string, linkId: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/pending/${encodeURIComponent(linkId)}/deny`,
    COMPANY_HR_OFFBOARD: (slug: string, linkId: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/offboard/${encodeURIComponent(linkId)}`,
    COMPANY_HR_LINK_UPDATE: (slug: string, linkId: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/links/${encodeURIComponent(linkId)}`,
    COMPANY_HR_INVITE_HR: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/invite-hr`,
    COMPANY_HR_HR_INVITES: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/hr-invites`,
    COMPANY_HR_ADD_EMPLOYEE: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/add-employee`,
    COMPANY_HR_RESEND_INVITE: (slug: string, invitationId: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    COMPANY_HR_INVITE_VALIDATE: `${API_BASE_URL}/api/subscriptions/company/hr-invite/validate`,
    COMPANY_HR_INVITE_ACCEPT: `${API_BASE_URL}/api/subscriptions/company/hr-invite/accept`,
    COMPANY_HR_BILLING: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/billing`,
    COMPANY_HR_CREATE_ORDER: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/payments/create-order`,
    COMPANY_HR_SETTINGS: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/settings`,
    COMPANY_HR_OFFBOARD_COMPANY: (slug: string) =>
      `${API_BASE_URL}/api/subscriptions/company/hr/${encodeURIComponent(slug)}/offboard-company`,
    COMPANY_COUNT: (type?: string) => {
      const base = `${API_BASE_URL}/api/subscriptions/company/count`;
      if (!type) {
        return base;
      }
      const query = encodeURIComponent(type);
      return `${base}?type=${query}`;
    },
    PROMO_CODES: `${API_BASE_URL}/api/subscriptions/promo-codes`,
    PROMO_CODE: (id: string) =>
      `${API_BASE_URL}/api/subscriptions/promo-codes/${encodeURIComponent(id)}`,
    PAYMENTS_ME: `${API_BASE_URL}/api/subscriptions/payments/me`,
    INVOICES_ME: `${API_BASE_URL}/api/subscriptions/invoices/me`,
    INVOICE_DOWNLOAD: (id: string) =>
      `${API_BASE_URL}/api/subscriptions/invoices/${encodeURIComponent(id)}/download-url`,
    CANCEL: (id: string) =>
      `${API_BASE_URL}/api/subscriptions/subscriptions/${encodeURIComponent(id)}/cancel`,
    REFUND: (id: string) =>
      `${API_BASE_URL}/api/subscriptions/subscriptions/${encodeURIComponent(id)}/refund`,
    ADMIN_PAYMENT_REVENUE_STATS: `${API_BASE_URL}/api/subscriptions/admin/payment-revenue-stats`,
    ADMIN_PLANS: `${API_BASE_URL}/api/subscriptions/admin/plans`,
    ADMIN_PLAN: (id: string) =>
      `${API_BASE_URL}/api/subscriptions/admin/plans/${encodeURIComponent(id)}`,
  },
  BILLING_ADDRESSES: {
    LIST: `${API_BASE_URL}/api/subscriptions/billing-addresses`,
    CREATE: `${API_BASE_URL}/api/subscriptions/billing-addresses`,
    UPDATE: `${API_BASE_URL}/api/subscriptions/billing-addresses`,
    DELETE: `${API_BASE_URL}/api/subscriptions/billing-addresses`,
    SET_DEFAULT: `${API_BASE_URL}/api/subscriptions/billing-addresses`,
  },
  NOTIFICATIONS: {
    LIST: `${API_BASE_URL}/api/notifications`,
    UNREAD_COUNT: `${API_BASE_URL}/api/notifications`,
    REMINDERS: `${API_BASE_URL}/api/notifications/reminders`,
  },
  FAMILY: {
    GROUP: `${API_BASE_URL}/api/v1/families/me`,
    MEMBERS: `${API_BASE_URL}/api/v1/families`,
    INVITE: `${API_BASE_URL}/api/v1/families`,
    ACCEPT_INVITE: `${API_BASE_URL}/api/v1/families`,
    DASHBOARD: `${API_BASE_URL}/api/v1/families`,
    SHARED_GOALS: `${API_BASE_URL}/api/v1/families`,
    GOALS: `${API_BASE_URL}/api/v1/families`,
  },
  SPLITMAGE: {
    GROUPS: `${API_BASE_URL}/api/splitmage/groups`,
    EXPENSES: `${API_BASE_URL}/api/splitmage/expenses`,
    BALANCES: `${API_BASE_URL}/api/splitmage/balances`,
    MEMBERS: `${API_BASE_URL}/api/splitmage/members`,
    INVITATIONS: `${API_BASE_URL}/api/splitmage/invitations`,
    ATTACHMENTS: `${API_BASE_URL}/api/splitmage/attachments`,
    SOCIAL: `${API_BASE_URL}/api/splitmage/social`,
    COMMENTS: `${API_BASE_URL}/api/splitmage/comments`,
    UPI: `${API_BASE_URL}/api/splitmage/upi`,
    PAYMENTS: `${API_BASE_URL}/api/splitmage/payments`,
    NETTING: `${API_BASE_URL}/api/splitmage/netting`,
    THREE_WAY: `${API_BASE_URL}/api/splitmage/three-way`,
    RECURRING: `${API_BASE_URL}/api/splitmage/recurring`,
    INSIGHTS: `${API_BASE_URL}/api/splitmage/insights`,
  },
  BLOG: {
    COMMENTS: `${API_BASE_URL}/api/blog/comments`,
    COMMENT_MODERATION: `${API_BASE_URL}/api/blog/comments/moderation`,
    COMMENT_ENGAGEMENT: (commentId: string) =>
      `${API_BASE_URL}/api/blog/comments/${encodeURIComponent(commentId)}/engagement/me`,
    COMMENT_VOTE: (commentId: string) =>
      `${API_BASE_URL}/api/blog/comments/${encodeURIComponent(commentId)}/vote`,
    COMMENT_REACT: (commentId: string) =>
      `${API_BASE_URL}/api/blog/comments/${encodeURIComponent(commentId)}/react`,
    MODERATION_VERSIONS: `${API_BASE_URL}/api/blog/moderation/versions`,
    MODERATION_VERSION: (versionId: string) =>
      `${API_BASE_URL}/api/blog/moderation/versions/${encodeURIComponent(versionId)}`,
    MODERATION_POST_DELETE: (postId: string) =>
      `${API_BASE_URL}/api/blog/moderation/posts/${encodeURIComponent(postId)}/delete`,
    COLLECTIONS: `${API_BASE_URL}/api/blog/collections`,
    COLLECTION: (collectionId: string) =>
      `${API_BASE_URL}/api/blog/collections/${encodeURIComponent(collectionId)}`,
    LEADERBOARD: `${API_BASE_URL}/api/blog/leaderboard`,
    EXPERT_LEVELS: `${API_BASE_URL}/api/blog/experts/levels`,
    SHORT_URL: `${API_BASE_URL}/api/blog/short-url`,
    POSTS: `${API_BASE_URL}/api/blog/posts`,
    POST_BY_SLUG: `${API_BASE_URL}/api/blog/posts`,
    AUTHORS: `${API_BASE_URL}/api/blog/authors`,
    COMMENT_COUNTS: `${API_BASE_URL}/api/blog/comments/counts`,
    PENDING_POSTS: `${API_BASE_URL}/api/blog/posts/pending`,
    AWARD_POINTS: `${API_BASE_URL}/api/blog/award-points`,
    MY_SUBMISSIONS: `${API_BASE_URL}/api/blog/submissions/me`,
    SUBMISSION_VERSION: (versionId: string) =>
      `${API_BASE_URL}/api/blog/submissions/versions/${encodeURIComponent(versionId)}`,
    SUBMIT_EDIT: `${API_BASE_URL}/api/blog/submissions/submit`,
    POST_LIKES_ME: (postId: string) =>
      `${API_BASE_URL}/api/blog/posts/${encodeURIComponent(postId)}/likes/me`,
    POST_LIKES: (postId: string) =>
      `${API_BASE_URL}/api/blog/posts/${encodeURIComponent(postId)}/likes`,
    TRACK_SHARE: (postId: string) =>
      `${API_BASE_URL}/api/blog/posts/${encodeURIComponent(postId)}/track-share`,
    ADMIN_TOPICS: `${API_BASE_URL}/api/blog/admin/topics`,
    ADMIN_MODERATOR_TOPICS: `${API_BASE_URL}/api/blog/admin/moderator-topics`,
    ADMIN_MODERATOR_USER_TOPICS: (userId: string) =>
      `${API_BASE_URL}/api/blog/admin/moderators/${encodeURIComponent(userId)}/topics`,
    ADMIN_MODERATORS: `${API_BASE_URL}/api/blog/admin/moderators`,
  },
  REFERRALS: {
    BASE: `${API_BASE_URL}/api/referrals`,
    CODE: `${API_BASE_URL}/api/referrals/referral-codes/me`,
    STATS: `${API_BASE_URL}/api/referrals/referrals/stats`,
    LIST: `${API_BASE_URL}/api/referrals/referrals/list`,
    REWARDS: `${API_BASE_URL}/api/referrals/referrals/rewards`,
    ATTRIBUTION_TRACK: `${API_BASE_URL}/api/referrals/referrals/attribution/track`,
    ATTRIBUTION_PROCESS: `${API_BASE_URL}/api/referrals/referrals/attribution/process`,
    CHECK_DELETED_USER: `${API_BASE_URL}/api/referrals/referrals/check-deleted-user`,
    ADMIN_OVERVIEW: `${API_BASE_URL}/api/referrals/referrals/admin/overview`,
    ADMIN_LEADERBOARD: `${API_BASE_URL}/api/referrals/referrals/admin/leaderboard`,
    ADMIN_PENDING: `${API_BASE_URL}/api/referrals/referrals/admin/pending-qualifications`,
  },
};

export const getAuthHeader = (token?: string) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};
